// 계약 관리 — 값의 한글 이름표 · 기준일/남은 날 글자 · 실패 문구(목록·상세 공용 · C2b 등록 창도 쓴다).
// 값 목록은 서버 backend/routers/contracts.py 와 같다(category A~I · region · party_type · term_type).
// 이름은 원 앱(app/dashboard.tsx)의 것을 그대로 쓴다.

export const CATEGORY_LABELS = {
  A: '매출(화주사)', B: '임대차', C: '도급·인력', D: '운송·택배', E: '부자재 구매',
  F: '장비·렌탈', G: 'IT·시스템', H: '상품 매입·총판', I: '기타',
};
export const REGION_LABELS = { KR: '국내', JP: '일본', US: '미국', EU: '유럽', CN: '중국', OT: '기타' };
export const PARTY_LABELS = { B: '기업', C: '개인', G: '정부·공공기관' };
export const TERM_LABELS = { S: '단기', L: '장기', P: '무기한' };

// 서버 _LABELS 와 같은 16칸.
export const FIELD_LABELS = {
  contract_no: '계약번호', category: '분류', region: '지역', party_type: '당사자 구분',
  term_type: '기간 유형', contract_name: '계약명', counterparty: '상대방', center: '센터',
  department: '부서', owner_name: '담당자', sub_owner_name: '부담당자', start_date: '시작일',
  end_date: '종료일', notice_date: '해지통보일', auto_renewal: '자동연장', summary: '요약',
};

// 이력의 action — 상세에 안 나오는 전역 action(grant_edit 등)도 C2c 이력 화면이 같이 쓴다.
export const EVENT_LABELS = {
  create: '등록', update: '수정', file_replace: '원본 교체', trash: '휴지통으로', restore: '복원',
  purge: '완전 삭제', view_file: '열어 봄', download: '내려받음', export: '내보내기',
  grant_edit: '편집 권한 줌', revoke_edit: '편집 권한 거둠', dept_add: '부서 추가',
  dept_rename: '부서 이름 바꿈', dept_delete: '부서 삭제', notify_settings: '알림 설정',
};

export const labelOf = (map, code) => map[code] || code || '—';

export const eventLabel = (action) => EVENT_LABELS[action] || action || '';

export function dueText(item) {
  if (!item || !item.due_date) return '—';
  return `${item.due_kind === 'notice' ? '해지통보' : '종료'} ${item.due_date}`;
}

// 남은 날은 서버가 계산한 값을 그대로 쓴다(화면에서 다시 계산하지 않는다).
export function daysText(daysLeft) {
  if (daysLeft === null || daysLeft === undefined) return '—';
  const n = Number(daysLeft);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '오늘';
  if (n > 0) return `D-${n}`;
  return `${Math.abs(n)}일 지남`;
}

// 30일 안(0 «오늘»·음수 «지남» 포함)이면 강조한다.
export const isUrgent = (daysLeft) => daysLeft !== null && daysLeft !== undefined && daysLeft <= 30;

const VALUE_LABELS = {
  category: CATEGORY_LABELS, region: REGION_LABELS, party_type: PARTY_LABELS, term_type: TERM_LABELS,
};

// 이력의 전/후 값은 서버가 날값으로 준다(분류 «A», 자동연장 «true») → 이름표로 바꿔 보인다.
export function eventValueText(field, value) {
  if (value === null || value === undefined || value === '') return '—';
  const text = String(value);
  if (VALUE_LABELS[field]) return VALUE_LABELS[field][text] || text;
  if (field === 'auto_renewal') {
    if (text === 'true') return '예';
    if (text === 'false') return '아니오';
  }
  return text;
}

// jsdom 16 에는 Blob.text() 가 없다 → FileReader 갈래로 읽는다(AbcAnalysisPage 와 같다).
const readBlobText = (blob) => (typeof blob.text === 'function'
  ? blob.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  }));

// 🔴 화면의 모든 실패 문구는 이것을 거친다.
//    responseType 'blob' 요청은 오류 본문도 Blob 이라 data.detail 이 늘 undefined 다 → 본문을 읽어 detail 을 꺼낸다.
//    detail 이 글자가 아니면(422 는 객체 목록) 대체 문구 — 그대로 그리면 React 가 깨진다.
export async function errorText(err, fallback) {
  const data = err?.response?.data;
  let detail;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const text = await readBlobText(data);
      try { detail = JSON.parse(text)?.detail; } catch { /* 파싱 실패면 대체 문구 */ }
    } catch { /* 읽기 실패면 대체 문구 */ }
  } else {
    detail = data?.detail;
  }
  return typeof detail === 'string' && detail.trim() ? detail : fallback;
}

// ── 기한 알림(C4) ──────────────────────────────────────────────────
// 글 항목 — 서버 contract_notify.FIELD_KEYS 와 같은 여섯(체크 상자 순서는 응답 field_keys 를 따른다).
export const NOTIFY_FIELD_LABELS = {
  category: '분류', contract_name: '계약명', counterparty: '상대방', owner: '담당자 이름', department: '주관 부서', link: '링크',
};

// 알림 지점 — 기준일 n 일 전(0 = 당일).
export function offsetText(days) {
  const n = Number(days);
  if (days === null || days === undefined || !Number.isFinite(n)) return '—';
  return n === 0 ? '당일' : `${n}일 전`;
}

// 발송 기록 한 행의 결과 — 🔴 detail 앞머리로만 가른다(한국어 문장 비교 금지 · 서버 contract_notify D_*).
//    표식 행(skip:*)도 ok 는 거짓으로 저장된다 → ok 보다 앞머리를 먼저 본다.
//    실패 사유는 showReason(시스템 관리자)일 때만 붙인다.
export function noticeResultText(notice, showReason) {
  const detail = typeof notice?.detail === 'string' ? notice.detail : '';
  if (detail.startsWith('post:')) return '보냄';
  if (detail.startsWith('fail:')) {
    const reason = detail.slice('fail:'.length).trim();
    return showReason && reason ? `실패 — ${reason}` : '실패';
  }
  if (detail.startsWith('skip:first')) return '보내지 않음(첫 실행)';
  if (detail.startsWith('skip:past')) return '보내지 않음(지난 지점)';
  if (!detail && notice?.ok !== true) return '보내는 중';
  // 앞머리 없는 글자는 서버가 쓰지 않는다 — 혹시 있으면 사유처럼 관리자에게만 그대로 보인다.
  return showReason && detail ? detail : '—';
}

// 마지막 실행 {date, sent, ok, detail} — 🔴 «보낼 계약 없음»은 detail 글자가 아니라 수와 ok 로 가른다.
export function lastRunText(lr) {
  if (!lr || typeof lr !== 'object') return '아직 실행되지 않았습니다.';
  const head = lr.date || '날짜 미상';
  const detail = lr.detail || '';
  if (lr.ok === true && Number(lr.sent) === 0) return `${head} · 보낼 계약 없음`;
  if (lr.ok === true) return `${head} · ${Number(lr.sent) || 0}건 보냄`;
  if (lr.ok === false) return `${head} · 실패 — ${detail}`;
  return `${head} · 보내지 않음 — ${detail}`;
}

// 파일 크기 — SI(1000) 기준.
export function fileSizeText(bytes) {
  const n = Number(bytes);
  if (bytes === null || bytes === undefined || !Number.isFinite(n) || n < 0) return '';
  if (n < 1000) return `${n} B`;
  if (n < 1000 ** 2) return `${(n / 1000).toFixed(1)} kB`;
  if (n < 1000 ** 3) return `${(n / 1000 ** 2).toFixed(1)} MB`;
  return `${(n / 1000 ** 3).toFixed(1)} GB`;
}
